import { useState } from "react";
import { Screen } from "../components/Screen";
import { api } from "../api/client";
import { track } from "../analytics/track";
import type { FuelType, Transmission } from "@karat/shared";

/**
 * п.13-14 ТЗ: приймаємо дані авто клієнта БЕЗ автоматичної оцінки (в проєкті немає
 * валідного алгоритму — і вигадувати його ми не будемо). Заявка йде консультанту,
 * клієнту одразу повідомляємо, що оцінку виконає спеціаліст (п.14, крок 3-4).
 */
export function TradeIn() {
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [mileageKm, setMileageKm] = useState("");
  const [transmission, setTransmission] = useState<Transmission>("AT");
  const [fuelType, setFuelType] = useState<FuelType>("petrol");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [vin, setVin] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = make && model && year && mileageKm && clientName && clientPhone;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/trade-in", {
        make,
        model,
        year: Number(year),
        mileageKm: Number(mileageKm),
        transmission,
        fuelType,
        driveType: null,
        vin: vin || null,
        clientName,
        clientPhone,
        photoUrls: [],
      });
      track("start_trade_in", { submitted: true });
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося надіслати заявку");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Screen title="Заявку прийнято" showBack>
        <div className="empty-state">
          <div className="empty-state-icon">✅</div>
          Дякуємо! Остаточну оцінку вашого автомобіля виконає спеціаліст дилерського центру —
          з вами зв'яжуться найближчим часом. Після підтвердження оцінки ви зможете використати
          цю суму як аванс при розрахунку кредиту.
        </div>
      </Screen>
    );
  }

  return (
    <Screen title="Trade-in / Оцінка авто" subtitle="Використайте власний автомобіль як аванс" showBack>
      <div className="field">
        <label>Марка</label>
        <input value={make} onChange={(e) => setMake(e.target.value)} placeholder="Toyota" />
      </div>
      <div className="field">
        <label>Модель</label>
        <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="RAV4" />
      </div>
      <div className="field">
        <label>Рік випуску</label>
        <input value={year} onChange={(e) => setYear(e.target.value)} inputMode="numeric" placeholder="2020" />
      </div>
      <div className="field">
        <label>Пробіг, км</label>
        <input value={mileageKm} onChange={(e) => setMileageKm(e.target.value)} inputMode="numeric" placeholder="80000" />
      </div>
      <div className="field">
        <label>Коробка передач</label>
        <div className="chip-row">
          {(["AT", "MT", "CVT", "AMT"] as Transmission[]).map((t) => (
            <button key={t} className={`chip ${transmission === t ? "active" : ""}`} onClick={() => setTransmission(t)}>
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <label>Паливо</label>
        <div className="chip-row">
          {(
            [
              ["petrol", "Бензин"],
              ["diesel", "Дизель"],
              ["hybrid", "Гібрид"],
              ["electric", "Електро"],
            ] as [FuelType, string][]
          ).map(([value, label]) => (
            <button key={value} className={`chip ${fuelType === value ? "active" : ""}`} onClick={() => setFuelType(value)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <label>VIN (необов'язково)</label>
        <input value={vin} onChange={(e) => setVin(e.target.value)} placeholder="VIN-код" />
      </div>

      <div className="section-title">Ваші контакти</div>
      <div className="field">
        <label>Ім'я</label>
        <input value={clientName} onChange={(e) => setClientName(e.target.value)} />
      </div>
      <div className="field">
        <label>Телефон</label>
        <input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="+380" />
      </div>

      {error && <div style={{ color: "var(--color-primary)", marginBottom: 12 }}>{error}</div>}
      <button className="btn btn-primary" disabled={!canSubmit || submitting} onClick={submit}>
        {submitting ? "Надсилаємо…" : "Надіслати на оцінку"}
      </button>
    </Screen>
  );
}
